import type { JSX } from 'react';
import { PortalHeader } from '@cca/ui';
import type { Identity, StandingSitting } from './api/client';
import { BallotList } from './cast/BallotList';
import { portalNameFor } from './portal-name';

// The first screen a signed-in person sees: who the SERVER says they are, what
// the register says they may do, and — new with the cast path — what they may
// vote in. Nothing else.
//
// ── TWO REGISTERS, ASKED SEPARATELY, AND THAT IS THE DESIGN ────────────────
// `identity.sittings` is the GRANT register: Admin and Scrutineer, the people
// who run and watch a sitting. `identity.electorSittings` is the frozen ROLL:
// members of Synod, who vote in one. They are different books and a person can
// be in either, both or neither (registrar ruling D2, 2026-08-19 — standing to
// vote comes from the roll and never from `role_grant`). So this screen shows
// two sections, each keyed on its own list, and the "nothing here for you"
// sentence is reached only when BOTH are empty. Before the cast path it was
// keyed on `sittings` alone, which would now tell every elector in Synod that
// they hold no role — true, and exactly the wrong thing to say to somebody
// holding a paper.
//
// ── IT SHOWS NO FIGURE OF ANY KIND ─────────────────────────────────────────
// No tally, no turnout, no count state. Registrar ruling, 2026-08-16: nothing
// that discloses vote numbers, percentages or results is ever made known beyond
// the Admin and the Scrutineers — and the way this screen honours that is by
// carrying nothing numeric at all, rather than by gating numbers it has fetched.
// Neither `GET /api/auth/me` nor `GET /api/me/ballots` returns a figure, so
// there is none here to leak: a screen that cannot say a number cannot be made
// to say one by the next person in a hurry.
//
// The LINKS it carries are the attendance register — only on a sitting the
// server said `admin` for on this request — and one per open ballot. A link is
// not an authorisation: each of those routes re-resolves standing on the request
// that asks and refuses anybody it does not seat. Showing them is courtesy, and
// hiding them from everybody else is not the control.
//
// ── EVERY WORD ON IT COMES FROM THE SERVER ─────────────────────────────────
// The role beside each sitting is `role_grant.role` resolved server-side on this
// request. It is not stored, not cached and not inferred from anything the
// browser knows — so a grant withdrawn between two page loads is gone from this
// list on the second one.

/** The two roles, in the words the schema's CHECK uses, turned into a sentence a
 *  person can act on. Two roles, two sentences, and no default arm: an
 *  unrecognised role never reaches here (the worker drops it), and a `default`
 *  that invented a description would be the one place a wrong word could appear
 *  authoritative. */
const ROLE_DESCRIPTION: Record<StandingSitting['role'], string> = {
  admin: 'You run this sitting: you may open and close ballots and read the counted sheets.',
  scrutineer: 'You watch this sitting: you may read the counted sheets.',
};

const ROLE_LABEL: Record<StandingSitting['role'], string> = {
  admin: 'Admin',
  scrutineer: 'Scrutineer',
};

/** A rehearsal or sandbox sitting must never be mistaken for the real one. The
 *  mode band on the results sheet makes the same point in the same words; this
 *  is the same rule applied one screen earlier, where the choice of sitting is
 *  actually made. */
const MODE_NOTE: Record<string, string> = {
  rehearsal: 'Rehearsal — not a live sitting. Nothing here binds Synod.',
  sandbox: 'Sandbox — practice data. Nothing here binds Synod.',
};

interface Props {
  identity: Identity;
  onSignOut: () => void;
  /** Whether a sign-out request is in flight, so the button cannot be pressed
   *  twice and says what it is doing. */
  signingOut: boolean;
}

export function Landing({ identity, onSignOut, signingOut }: Props): JSX.Element {
  // Absent means "this server does not answer that question" — see the field's
  // note in api/client.ts. It reads as no roll standing, which is the safe way
  // round: a member is shown no ballot section rather than an empty one.
  const electorSittings = identity.electorSittings ?? [];

  return (
    <div className="ballot-app min-dvh">
      {/* The name is the reader's own: the Admin who runs a sitting is shown
          "Decision Dashboard", everybody else "Decision Portal". It is decided
          from the role the SERVER put on this identity and in one place
          (./portal-name.ts) — a second spelling is how two screens come to
          disagree. It draws a word; it authorises nothing. */}
      <PortalHeader portalName={portalNameFor(identity)} />

      <main className="ballot-main">
        <div className="page-narrow">
          {/* THE PAGE'S OWN TITLE, and this screen's only <h1>. The
              ballot paper, the attendance register and the results sheet all open
              with one (`.ballot-title` / `.result-title`); this screen opened with
              an <h2>, so it read smaller than every screen it links to and the
              heading order started at level 2. `PortalHeader` is deliberately not
              the fix: it renders the portal name in a <p> — chrome, identical
              across all 22 consumers — and a shared header must not become one
              app's page title.

              It carries `portalNameFor(identity)`, the SAME word the header
              shows, from the SAME single source (./portal-name.ts). Two places,
              one decision: a title that disagreed with the header above it is
              exactly the drift that module exists to prevent. It draws a word; it
              authorises nothing. */}
          <h1 className="ballot-title">{portalNameFor(identity)}</h1>

          <h2>Signed in</h2>
          <p>
            You are signed in as <strong>{identity.email}</strong>.
          </p>

          {/* THE BALLOTS FIRST, because for a member of Synod they are the whole
              reason the page exists — and during a sitting they are the only
              thing on it that is time-sensitive. Shown only to somebody the roll
              seats: an Admin who is not an elector has no ballot section, and
              the section itself carries no figure. */}
          {electorSittings.length > 0 && (
            <>
              <h3>Your ballots</h3>
              <BallotList />
            </>
          )}

          {identity.sittings.length > 0 && (
            <>
              <h3>What you may do</h3>
              <ul>
                {identity.sittings.map((sitting) => (
                  <li key={`${sitting.electionId} ${sitting.mode}`}>
                    <strong>{sitting.name}</strong> — {ROLE_LABEL[sitting.role]}
                    <br />
                    {ROLE_DESCRIPTION[sitting.role]}
                    {MODE_NOTE[sitting.mode] !== undefined && (
                      <>
                        <br />
                        <em>{MODE_NOTE[sitting.mode]}</em>
                      </>
                    )}
                    {/* THE ATTENDANCE REGISTER — offered only where the SERVER
                        said `admin` on this very request. That is not the gate:
                        the route re-resolves the role on the request that asks
                        and refuses a Scrutineer `not_administrator`. Hiding the
                        link is courtesy, not control — the point is that a
                        Scrutineer is not shown a door they will be turned away
                        from. */}
                    {sitting.role === 'admin' && (
                      <>
                        {/* THE SETUP HUB — an admin-only peer of the register
                            link, and offered only where the SERVER said `admin`
                            on this request. Like the register link it is courtesy,
                            not control: the setup route re-resolves the grant and
                            refuses anybody it does not seat. */}
                        <br />
                        <a href={`#setup/${encodeURIComponent(sitting.electionId)}`}>
                          Set up the roll and ballots
                        </a>
                        <br />
                        <a href={`#register/${encodeURIComponent(sitting.electionId)}`}>
                          Open the attendance register
                        </a>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* BOTH BOOKS EMPTY. Reachable only if a grant is withdrawn or a
              member is stood down between the sign-in and this load — signing in
              requires standing in one of the two. Said as a fact rather than as
              an error, because it is not the reader's mistake. */}
          {electorSittings.length === 0 && identity.sittings.length === 0 && (
            <p className="alert">
              Your address no longer holds a role in any sitting, and is not on the roll for one. If
              that is unexpected, contact{' '}
              <a href="mailto:registrar@confessinganglicans.nz">registrar@confessinganglicans.nz</a>
              .
            </p>
          )}

          {/* THE PREVIEWS ARE STILL PREVIEWS. They render invented fixtures and
              call nothing, which is the only reason they are safe to show at
              all. Wiring these to real ballot data is a later, separate change
              and must go through GET /api/results/:ballotId and no other route —
              see the deployment condition in the README.

              THEY ARE NOT SHOWN TO A MEMBER WHO ONLY VOTES, and that is a safety
              decision rather than tidiness. A member of Synod in a pew, told that
              voting is open, is now shown a real ballot — and "The ballot paper"
              directly beneath it is a fixture that accepts a mark and records
              nothing. Its banner says so, and a person in a hurry does not read
              banners. An Admin or a Scrutineer knows what a design preview is;
              a voter has no reason to. */}
          {identity.sittings.length > 0 && (
            <>
              <h3>Design previews</h3>
              <p className="alert ballot-preview-notice">
                <strong>Sample data.</strong> These screens render invented fixtures and are not
                connected to any sitting. No ballot is open and nothing you do on them is recorded.
              </p>
              <ul>
                <li>
                  <a href="#paper">The ballot paper</a>
                </li>
                <li>
                  <a href="#motions">Motions counted by Houses</a>
                </li>
                <li>
                  <a href="#one-tally">A motion counted as one tally</a>
                </li>
                <li>
                  <a href="#elections">Elections</a>
                </li>
              </ul>
            </>
          )}

          <button type="button" className="btn btn-ghost" onClick={onSignOut} disabled={signingOut}>
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </main>
    </div>
  );
}
